/**
 * @file utils.js
 * @description Utility functions for form composables
 * @version 1.0.0
 */

// ============================================================================
// Object Manipulation Functions
// ============================================================================

/**
 * Helper function to omit properties from object
 *
 * @template T
 * @param {T} obj
 * @param {string[]} keys
 * @returns {Partial<T>}
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = omit(obj, ['b', 'c']); // { a: 1 }
 */
export function omit(obj, keys) {
  const result = { ...obj };
  keys.forEach(key => delete result[key]);
  return result;
}

/**
 * Helper function to pick properties from object
 *
 * @template T
 * @param {T} obj
 * @param {string[]} keys
 * @returns {Partial<T>}
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = pick(obj, ['a', 'c']); // { a: 1, c: 3 }
 */
export function pick(obj, keys) {
  const result = {};
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

// ============================================================================
// Nested Object Functions
// ============================================================================

/**
 * Utility to get nested value with type safety
 * Supports dot notation path (e.g., 'user.profile.name')
 *
 * @template T
 * @param {T} obj
 * @param {string} path
 * @returns {any}
 * @example
 * const form = { user: { profile: { name: 'John' } } };
 * const name = getNestedValue(form, 'user.profile.name'); // 'John'
 */
export function getNestedValue(obj, path) {
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current == null) return undefined;
    current = current[key];
  }

  return current;
}

/**
 * Utility to set nested value with type safety
 * Supports dot notation path (e.g., 'user.profile.name')
 * Creates intermediate objects if they don't exist
 *
 * @template T
 * @param {T} obj
 * @param {string} path
 * @param {any} value
 * @returns {void}
 * @example
 * const form = { user: { profile: {} } };
 * setNestedValue(form, 'user.profile.name', 'John');
 * // form.user.profile.name === 'John'
 */
export function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
}

// ============================================================================
// Validation Schema Functions
// ============================================================================

/**
 * Recursively flatten nested validation schema to field paths
 * Converts nested schema to flat object with dot notation keys
 *
 * @param {Object} schema - Validation schema (possibly nested)
 * @param {string} prefix - Current path prefix
 * @returns {Object} Flattened schema
 * @example
 * const schema = {
 *   user: {
 *     profile: {
 *       name: validators.required()
 *     }
 *   }
 * };
 * const flat = flattenValidationSchema(schema);
 * // { 'user.profile.name': validators.required() }
 */
export function flattenValidationSchema(schema, prefix = '') {
  const result = {};

  Object.entries(schema).forEach(([key, value]) => {
    const fullPath = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'function') {
      // It's a validator function
      result[fullPath] = value;
    } else if (value && typeof value === 'object') {
      // It's a nested schema object
      Object.assign(result, flattenValidationSchema(value, fullPath));
    }
  });

  return result;
}

/**
 * Get all possible field paths from a nested object
 * Used to initialize errors and touched state
 *
 * @param {Object} obj - Form object
 * @param {string} prefix - Current path prefix
 * @returns {string[]} Array of field paths
 * @example
 * const form = { user: { profile: { name: '' } } };
 * const paths = getAllFieldPaths(form);
 * // ['user', 'user.profile', 'user.profile.name']
 */
export function getAllFieldPaths(obj, prefix = '') {
  const paths = [];

  Object.entries(obj).forEach(([key, value]) => {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    paths.push(fullPath);

    // Don't traverse into arrays or objects with 'id' property (lookup fields)
    if (value && typeof value === 'object' && !Array.isArray(value) && !('id' in value)) {
      paths.push(...getAllFieldPaths(value, fullPath));
    }
  });

  return paths;
}

// ============================================================================
// Deep Clone & Comparison Functions
// ============================================================================

/**
 * Deep clone object using structuredClone if available, fallback to JSON
 * Fast and handles more types than JSON.parse/stringify
 * Supports: Date, RegExp, Map, Set, ArrayBuffer, etc. (in modern browsers)
 *
 * @template T
 * @param {T} obj
 * @returns {T}
 * @example
 * const original = { user: { name: 'John', createdAt: new Date() } };
 * const clone = deepClone(original);
 * clone.user.name = 'Jane';
 * // original.user.name === 'John' (unchanged)
 */
export function deepClone(obj) {
  // Use structuredClone if available (modern browsers)
  if (typeof structuredClone === 'function') {
    return structuredClone(obj);
  }

  // Fallback to JSON for older browsers
  // Note: This doesn't handle Date, RegExp, etc. properly
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if two objects are deeply equal
 * Used for change detection to prevent unnecessary updates
 *
 * @param {any} obj1
 * @param {any} obj2
 * @returns {boolean}
 * @example
 * const obj1 = { user: { name: 'John' } };
 * const obj2 = { user: { name: 'John' } };
 * isDeepEqual(obj1, obj2); // true
 */
export function isDeepEqual(obj1, obj2) {
  // Same reference or both primitive and equal
  if (obj1 === obj2) return true;

  // One is null/undefined
  if (obj1 == null || obj2 == null) return obj1 === obj2;

  // Different types
  if (typeof obj1 !== typeof obj2) return false;

  // Both are primitive (not objects)
  if (typeof obj1 !== 'object') return obj1 === obj2;

  // Different array/object types
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  // Compare object keys
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  // Recursively compare all keys
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!isDeepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

// ============================================================================
// Batch Update Function
// ============================================================================

/**
 * Update multiple form fields at once
 * Useful for bulk updates or computed fields
 *
 * @param {Object} form - Form object
 * @param {Object} updates - Object with field paths and values
 * @returns {void}
 * @example
 * batchUpdate(form, {
 *   'user.name': 'John',
 *   'user.email': 'john@example.com',
 *   'user.age': 30
 * });
 */
export function batchUpdate(form, updates) {
  Object.entries(updates).forEach(([path, value]) => {
    setNestedValue(form, path, value);
  });
}

// ============================================================================
// Debounce Function (Custom - for Vue 2.7 compatibility)
// ============================================================================

/**
 * Create a debounced function that delays execution until after wait milliseconds
 * have elapsed since the last time it was invoked
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} Debounced function with cancel method
 * @example
 * const debouncedValidate = debounce(() => {
 *   validateField('email');
 * }, 500);
 *
 * // Call multiple times, only last call executes after 500ms
 * debouncedValidate();
 * debouncedValidate();
 * debouncedValidate();
 *
 * // Cancel pending execution
 * debouncedValidate.cancel();
 */
export function debounce(func, wait = 300) {
  let timeoutId = null;

  const debounced = function (...args) {
    const context = this;

    // Clear existing timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    // Set new timeout
    timeoutId = setTimeout(() => {
      timeoutId = null;
      func.apply(context, args);
    }, wait);
  };

  // Add cancel method
  debounced.cancel = function () {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}
